import { useEffect, useMemo } from 'react';
import { Router } from '@makemydeal/dr-react-18-wrappers';

import { ThemeProvider } from 'styled-components';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import { RootComponent, type TradeInStepName } from '@makemydeal/dr-activities-tradein';

import { TradeInComponentContainer } from './Root.style';
import { useDispatch, useSelector } from 'react-redux';
import { routingConfig } from '@makemydeal/dr-sales-checkin-types';
import { getTradeInSelectors, getTradeInConfig, middlewareMgr } from '../store/config';
import { tradeInHistory } from '../store/tradeInHistory';
import type { TradeInRootProps } from '../types/ITradeinActivity';
import { getTradeInBaseRoute, hasSavedTradeIn, isTradeInCompleted } from '../store';
import { tradeInStepChangeActionCreator } from '../store/actionCreators';

const TradeInRoot = (props: TradeInRootProps) => {
    const { isSalesView = false } = props;
    const theme = useInterstateTheme();
    const dispatch = useDispatch();
    const dealerDetails = useSelector((state: any) =>
        middlewareMgr?.sciSelectors?.additionalSelectors.useDealerDetailsSelector(state)
    );

    // Get selectors and config at render time (after init has been called)
    const selectors = getTradeInSelectors();
    const tradeInConfig = getTradeInConfig();

    // Resume the summary step for a saved trade-in (e.g. after refresh); otherwise start fresh.
    const savedTradeIn = useSelector(hasSavedTradeIn);
    const isCompleted = useSelector(isTradeInCompleted);
    const currentRoute = getTradeInBaseRoute(isCompleted);

    const config = useMemo(
        () => (tradeInConfig ? { ...tradeInConfig, userContext: { isDealerShowroomExperience: isSalesView } } : undefined),
        [tradeInConfig, isSalesView]
    );

    // Load required stylesheets on component mount
    useEffect(() => {
        const head = document.head;
        const assets: { id: string; href: string }[] = [
            {
                id: 'tradein-fontawesome-4.3.0',
                href: 'https://maxcdn.bootstrapcdn.com/font-awesome/4.3.0/css/font-awesome.min.css'
            },
            {
                id: 'tradein-bootstrap-3.3.7',
                href: 'https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css'
            }
        ];

        assets.forEach(({ id, href }) => {
            if (!document.getElementById(id)) {
                const link = document.createElement('link');
                link.id = id;
                link.rel = 'stylesheet';
                link.href = href;
                // Optional: mark as trade-in specific for debugging
                link.setAttribute('data-tradein-dynamic', 'true');
                head.appendChild(link);
            }
        });
    }, []);

    useEffect(() => {
        // Hide global spinner when dealer details are loaded
        if (dealerDetails && Object.keys(dealerDetails).length > 0) {
            dispatch(middlewareMgr.hostActions.hideGlobalSpinner());
        }
    }, [dealerDetails, dispatch]);

    // Don't render until selectors and config are initialized
    if (!selectors || !config) {
        return null;
    }

    return (
        <ThemeProvider theme={theme}>
            <TradeInComponentContainer className="dr-shopper-app">
                {/*
                  The trade-in flow (dr-activities-tradein) owns ABSOLUTE root-level routes
                  (e.g. /vehicleInfo, /tradeSummary) and navigates them via its own router.
                  The browser is mounted under /showroom/trade-in, which matches none of those
                  absolute routes, so RootComponent would render an empty container.
                  To mirror Sales View (shopperPlatform runs activities on a memory history),
                  give trade-in its own in-memory routing space decoupled from the browser URL.
                  Seed it at /tradeIn so RootComponent's internal redirect lands on the correct step.
                */}
                <Router history={tradeInHistory}>
                    <RootComponent
                        baseRoute={routingConfig.TRADE_IN}
                        config={config}
                        selector={selectors}
                        actionCreators={{}}
                        currentRoute={currentRoute}
                        onStepChange={(step: TradeInStepName) => dispatch(tradeInStepChangeActionCreator(step))}
                        isCompleted={savedTradeIn}
                    />
                </Router>
            </TradeInComponentContainer>
        </ThemeProvider>
    );
};

TradeInRoot.displayName = 'TradeinActivity';

export default TradeInRoot;
